import { useState } from "react";
import { motion } from "framer-motion";
import {
  Swords, Shield, Trophy, Bell, Users, Crown, Globe, Zap, Check,
  MessageCircle, Menu, X, ChevronRight, Star, Clock, BarChart3, Target,
  ShieldCheck, Lock, Cpu, Ban,
} from "lucide-react";
import { useBranding } from "@/hooks/useBranding";
import AutomatedAlerts from "../components/AutomatedAlerts";
import CommandCatalog from "../components/CommandCatalog";

const getWaLink = (brandName) => "https://wa.me/17868740733?text=" + encodeURIComponent(`Hola, quiero adquirir el bot ${brandName} para mi clan de Clash of Clans.`);

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`max-w-7xl mx-auto px-5 md:px-8 ${className}`}>{children}</section>
  );
}

function Nav() {
  const brandName = useBranding();
  const [open, setOpen] = useState(false);
  const links = [
    ["Características", "#features"],
    ["Avisos", "#alertas"],
    ["Seguridad", "#security"],
    ["Comandos", "#commands"],
    ["Planes", "#pricing"],
    ["FAQ", "#faq"],
  ];
  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-obsidian/80 backdrop-blur-xl border-b border-white/10" data-testid="landing-nav">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2.5">
          <img src="/logo.png?v=20260724-groups" alt={brandName} className="w-11 h-11 rounded object-contain" />
          <span className="font-heading font-bold text-lg tracking-tight" data-testid="nav-brand-name">{brandName}</span>
        </a>
        <div className="hidden md:flex items-center gap-8">
          {links.map(([t, h]) => (
            <a key={h} href={h} className="text-sm text-muted-foreground hover:text-white transition-colors">{t}</a>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-3">
          <a href="/admin" className="text-sm text-muted-foreground hover:text-white transition-colors">Panel</a>
          <a data-testid="nav-cta" href={getWaLink(brandName)} target="_blank" rel="noreferrer" className="bg-gold text-black font-bold text-sm rounded-md px-4 py-2 hover:brightness-95 transition-all flex items-center gap-1.5">
            <MessageCircle size={16} /> Adquirir
          </a>
        </div>
        <button className="md:hidden p-2" onClick={() => setOpen((v) => !v)} data-testid="landing-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-obsidian border-t border-white/10 px-5 py-4 space-y-3">
          {links.map(([t, h]) => (
            <a key={h} href={h} onClick={() => setOpen(false)} className="block text-muted-foreground hover:text-white">{t}</a>
          ))}
          <a href="/admin" className="block text-muted-foreground">Panel de administración</a>
          <a href={getWaLink(brandName)} target="_blank" rel="noreferrer" className="block bg-gold text-black font-bold rounded-md px-4 py-2 text-center">Adquirir tu plan</a>
        </div>
      )}
    </nav>
  );
}

function Hero() {
  const brandName = useBranding();
  return (
    <div id="top" className="relative pt-16 overflow-hidden">
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: "linear-gradient(90deg, rgba(10,10,10,.97) 0%, rgba(10,10,10,.85) 45%, rgba(10,10,10,.55) 100%), url('/hero.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <Section className="py-20 md:py-32">
        {/* En móvil/tablet (hasta lg) la imagen del menú se muestra primero, a
            todo lo ancho, como encabezado. Desde lg+ se oculta aquí y aparece
            al lado del texto (bloque original, sin cambios, más abajo). */}
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:hidden mb-8 relative"
          data-testid="hero-mobile-header-image"
        >
          <div className="absolute inset-0 rounded-2xl bg-gold/10 blur-3xl -z-10" />
          <div className="rounded-xl overflow-hidden border border-gold/25 glow-gold">
            <img
              src="/menu.png"
              alt={`${brandName} - Menú completo del bot`}
              className="w-full block"
            />
          </div>
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div initial="hidden" animate="show" variants={fade} className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-gold mb-6">
              <Star size={13} /> Bot #1 de Clash of Clans para WhatsApp
            </motion.div>
            <motion.h1 initial="hidden" animate="show" custom={1} variants={fade} className="font-heading font-extrabold text-4xl sm:text-6xl leading-[1.05] tracking-tight">
              Tu Clan.<br /><span className="text-gold">Tu Legado.</span><br />
              <span className="text-crimson">Al instante.</span>
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fade} className="text-muted-foreground text-lg mt-6 max-w-lg">
              {brandName} lleva las estadísticas de guerra, CWL, rankeds y avisos de ataques
              directamente al chat de WhatsApp de tu clan. Datos oficiales de Supercell, 24/7.
            </motion.p>
            <motion.p initial="hidden" animate="show" custom={2.4} variants={fade} className="text-sm text-emerald-400 font-medium mt-3 flex items-center gap-1.5">
              <ShieldCheck size={16} /> La misma potencia que un bot de Discord para PC — pero 100% nativo en WhatsApp, la app que tu clan ya usa cada día.
            </motion.p>
            <motion.div initial="hidden" animate="show" custom={3} variants={fade} className="flex flex-wrap gap-3 mt-8">
              <a data-testid="hero-cta" href={getWaLink(brandName)} target="_blank" rel="noreferrer" className="bg-gold text-black font-bold rounded-md px-6 py-3 hover:brightness-95 transition-all flex items-center gap-2">
                <MessageCircle size={18} /> Adquiere tu plan
              </a>
              <a href="#features" className="border border-white/20 rounded-md px-6 py-3 font-medium hover:bg-white/5 transition-all flex items-center gap-2">
                Ver qué hace <ChevronRight size={18} />
              </a>
            </motion.div>
            <motion.div initial="hidden" animate="show" custom={4} variants={fade} className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Check size={15} className="text-emerald-400" /> Multi-grupo independiente</span>
              <span className="flex items-center gap-1.5"><Check size={15} className="text-emerald-400" /> Datos en tiempo real</span>
              <span className="flex items-center gap-1.5"><Check size={15} className="text-emerald-400" /> Plan Elite $2.99</span>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.3 }} className="hidden lg:flex justify-center relative">
            <div className="absolute inset-0 rounded-2xl bg-gold/10 blur-3xl -z-10" />
            <div className="rounded-xl overflow-hidden border border-gold/25 glow-gold">
              <img
                src="/menu.png"
                alt={`${brandName} - Menú completo del bot`}
                className="w-full max-w-lg mx-auto block"
              />
            </div>
          </motion.div>
        </div>
      </Section>
    </div>
  );
}

function TrustBar() {
  const items = [
    ["100%", "Nativo en WhatsApp"],
    ["24/7", "Disponibilidad"],
    ["+70", "Comandos disponibles"],
    ["0", "Apps o PC necesarias"],
  ];
  return (
    <Section className="py-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map(([n, t], i) => (
          <motion.div key={t} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade} className="card-surface p-5 text-center">
            <div className="text-3xl font-heading font-extrabold text-gold">{n}</div>
            <div className="text-xs text-muted-foreground mt-1">{t}</div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

const FEATURES = [
  { icon: Swords, color: "text-crimson", bg: "bg-crimson/10", title: "Guerra en vivo", desc: "Estado de guerra, ataques pendientes, mejores atacantes, fallidos y limpieza pendiente." },
  { icon: Trophy, color: "text-gold", bg: "bg-gold/10", title: "Liga CWL", desc: "Resumen de CWL, top de la liga, rival actual y análisis completo ronda por ronda." },
  { icon: Bell, color: "text-emerald-400", bg: "bg-emerald-500/10", title: "Avisos de ataques", desc: "Notificaciones instantáneas o resúmenes por hora de cada ataque de tu clan y del rival." },
  { icon: Globe, color: "text-blue-400", bg: "bg-blue-500/10", title: "Rankeds global y local", desc: "Clasificación de jugadores y clanes, posición global y por país en tiempo real." },
  { icon: Users, color: "text-purple-400", bg: "bg-purple-500/10", title: "Multi-grupo", desc: "Cada grupo de WhatsApp vinculado a su propio clan, funcionando de forma independiente." },
  { icon: BarChart3, color: "text-gold", bg: "bg-gold/10", title: "Reportes de guerra", desc: "Estadísticas del mes actual y anterior: victorias, derrotas, estrellas y % de destrucción." },
];

function Features() {
  const brandName = useBranding();
  return (
    <Section id="features" className="py-20">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="overline text-gold">Todo lo que tu clan necesita</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Inteligencia estratégica en tu chat</h2>
        <p className="text-muted-foreground mt-3">Deja de saltar entre apps. {brandName} centraliza toda la información de tu clan en WhatsApp.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {FEATURES.map((f, i) => {
          const Icon = f.icon;
          return (
            <motion.div key={f.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade} className="card-surface card-hover p-6 h-full">
              <div className={`w-12 h-12 rounded-md flex items-center justify-center ${f.bg} ${f.color} mb-4`}>
                <Icon size={24} />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}

const STEPS = [
  { n: "01", icon: MessageCircle, title: "Contacta al admin", desc: "Escríbenos por WhatsApp al +1 786 874 0733 y elige tu plan." },
  { n: "02", icon: Shield, title: "Agrega el bot al grupo", desc: "Añadimos el bot a tu grupo y lo vinculamos al tag de tu clan." },
  { n: "03", icon: Zap, title: "Activamos tu licencia", desc: "En segundos tu clan tiene acceso a todos los comandos y avisos." },
  { n: "04", icon: Crown, title: "Domina la guerra", desc: "Tu clan recibe datos al instante y coordina como nunca antes." },
];

const SECURITY = [
  { icon: Ban, color: "text-emerald-400", bg: "bg-emerald-500/10", title: "100% libre de baneo", desc: "Solo consultamos la API oficial de desarrolladores de Supercell. No usamos mods, trucos ni automatización del juego: tu cuenta y tu clan están completamente seguros." },
  { icon: ShieldCheck, color: "text-gold", bg: "bg-gold/10", title: "API oficial de Supercell", desc: "Toda la información proviene de los servidores oficiales de Clash of Clans mediante un token de desarrollador autorizado. Datos reales y legítimos." },
  { icon: Clock, color: "text-blue-400", bg: "bg-blue-500/10", title: "Funcionamiento 24/7", desc: "El bot opera de forma continua, todos los días. Consultas y avisos disponibles a cualquier hora, sin interrupciones." },
  { icon: Lock, color: "text-purple-400", bg: "bg-purple-500/10", title: "Privado y seguro", desc: "Cada grupo está aislado con su propia licencia y configuración. No compartimos ni exponemos los datos de tu clan." },
];

function Security() {
  const brandName = useBranding();
  return (
    <Section id="security" className="py-20">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="overline text-gold">Confianza y seguridad</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Seguro, estable y <span className="text-emerald-400">libre de baneo</span></h2>
        <p className="text-muted-foreground mt-3">{brandName} no interactúa con tu partida: solo lee estadísticas públicas mediante la API oficial. Cero riesgo para tu cuenta.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {SECURITY.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade} className="card-surface card-hover p-6 h-full">
              <div className={`w-12 h-12 rounded-md flex items-center justify-center ${s.bg} ${s.color} mb-4`}>
                <Icon size={24} />
              </div>
              <h3 className="font-heading font-semibold text-lg mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </motion.div>
          );
        })}
      </div>
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
        className="mt-8 card-surface p-6 md:p-7 flex flex-col md:flex-row items-center gap-5 text-center md:text-left">
        <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0">
          <ShieldCheck size={28} />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-lg">Garantía de tranquilidad</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Miles de consultas se hacen a diario por la vía oficial de Supercell. Al no requerir tu contraseña ni acceder a tu cuenta de juego, tu progreso está 100% protegido.
          </p>
        </div>
        <div className="flex gap-6 shrink-0">
          <div className="text-center"><div className="text-2xl font-heading font-extrabold text-gold">0</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">Baneos</div></div>
          <div className="text-center"><div className="text-2xl font-heading font-extrabold text-emerald-400">99.9%</div><div className="text-[11px] text-muted-foreground uppercase tracking-wider">Uptime</div></div>
        </div>
      </motion.div>
    </Section>
  );
}

function HowItWorks() {
  return (
    <Section className="py-20">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="overline text-gold">Conexión rápida y segura</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Listo en 4 pasos</h2>
      </div>
      <div className="grid md:grid-cols-4 gap-5">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade} className="relative card-surface p-6 h-full">
              <div className="text-5xl font-heading font-extrabold text-white/5 absolute top-3 right-4">{s.n}</div>
              <div className="w-11 h-11 rounded-md bg-gold/10 text-gold flex items-center justify-center mb-4"><Icon size={22} /></div>
              <h3 className="font-heading font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground">{s.desc}</p>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}

const ELITE = [
  "Todos los comandos de guerra y clan",
  "Reportes de CWL y ranked (global y local)",
  "Avisos de ataques en tiempo real o resumen",
  "Avisos de nuevos miembros",
  "Análisis y pronóstico de guerra",
  "Info completa del rival CWL",
  "Soporte prioritario por WhatsApp",
];

function Pricing() {
  const brandName = useBranding();
  return (
    <Section id="pricing" className="py-20">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="overline text-gold">Planes</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">Potencia tu clan por muy poco</h2>
        <p className="text-muted-foreground mt-3">Un precio simple. Todo incluido. Sin complicaciones.</p>
      </div>
      <div className="max-w-md mx-auto">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade} className="card-surface glow-gold p-8 relative overflow-hidden" data-testid="pricing-card">
          <div className="absolute top-0 right-0 bg-gold text-black text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-bl-lg">Más popular</div>
          <div className="flex items-center gap-2 text-gold mb-2"><Crown size={20} /><span className="overline text-gold">Plan Elite</span></div>
          <div className="flex items-end gap-1 mb-1">
            <span className="text-5xl font-heading font-extrabold">$2.99</span>
            <span className="text-muted-foreground mb-1.5">/ mes</span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">Acceso completo para todo tu clan.</p>
          <div data-testid="pricing-extra-group" className="flex items-center gap-2.5 rounded-md border border-gold/30 bg-gold/5 px-3.5 py-2.5 mb-6">
            <Users size={18} className="text-gold shrink-0" />
            <p className="text-sm"><span className="font-bold text-gold">+$0.99</span> <span className="text-muted-foreground">por cada grupo adicional del mismo usuario</span></p>
          </div>
          <ul className="space-y-3 mb-8">
            {ELITE.map((e) => (
              <li key={e} className="flex items-start gap-2.5 text-sm">
                <Check size={18} className="text-emerald-400 shrink-0 mt-0.5" /> <span>{e}</span>
              </li>
            ))}
          </ul>
          <a data-testid="pricing-cta" href={getWaLink(brandName)} target="_blank" rel="noreferrer" className="w-full bg-gold text-black font-bold rounded-md py-3 flex items-center justify-center gap-2 hover:brightness-95 transition-all">
            <MessageCircle size={18} /> Adquirir por WhatsApp
          </a>
          <p className="text-center text-xs text-muted-foreground mt-4">📲 +1 786 874 0733</p>
        </motion.div>
      </div>
    </Section>
  );
}

const FAQS = [
  ["¿De dónde salen los datos?", "Directamente de la API oficial de Supercell (Clash of Clans), en tiempo real. Nada es inventado ni cacheado."],
  ["¿Funciona en varios grupos a la vez?", "Sí. Cada grupo de WhatsApp se vincula a su propio clan mediante su tag y funciona de forma totalmente independiente."],
  ["¿Cuánto cuesta agregar más grupos?", "El Plan Elite cuesta $2.99/mes. Si el mismo usuario quiere activar el bot en grupos adicionales, cada grupo extra cuesta solo $0.99/mes."],
  ["¿Necesito instalar algo?", "No. Solo agregas el bot a tu grupo de WhatsApp y nosotros activamos tu licencia. Los miembros solo escriben comandos como !war."],
  ["¿Qué pasa si no tengo plan activo?", "Sin plan activo el grupo solo puede usar !help y !help more. Al activar el plan, todos los miembros pueden usar todos los comandos."],
  ["¿Puedo recibir avisos de ataques?", "Sí, con !notify on. Elige modo instantáneo (aviso por cada ataque) o resumen cada X horas, y si quieres incluir al rival."],
  ["¿Cómo adquiero el bot?", "Escríbenos por WhatsApp al +1 786 874 0733 y te activamos el plan Elite para tu clan en minutos."],
];

function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <Section id="faq" className="py-20">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="overline text-gold">Preguntas frecuentes</span>
        <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">¿Dudas? Las resolvemos</h2>
      </div>
      <div className="max-w-3xl mx-auto space-y-3">
        {FAQS.map(([q, a], i) => (
          <div key={q} className="card-surface overflow-hidden" data-testid={`faq-${i}`}>
            <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 p-5 text-left">
              <span className="font-medium">{q}</span>
              <ChevronRight size={18} className={`shrink-0 transition-transform ${open === i ? "rotate-90 text-gold" : "text-muted-foreground"}`} />
            </button>
            {open === i && <div className="px-5 pb-5 text-sm text-muted-foreground -mt-1">{a}</div>}
          </div>
        ))}
      </div>
    </Section>
  );
}

function FinalCta() {
  const brandName = useBranding();
  return (
    <Section className="py-16">
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
        className="card-surface glow-gold p-10 md:p-14 text-center relative overflow-hidden"
        style={{ backgroundImage: "linear-gradient(rgba(18,18,20,.9),rgba(18,18,20,.95)), url('/hero.png')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <Crown className="mx-auto text-gold mb-4" size={40} />
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl">Lleva tu clan al siguiente nivel</h2>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Únete a los clanes que ya coordinan sus guerras con {brandName}. Activación en minutos.</p>
        <a href={getWaLink(brandName)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-gold text-black font-bold rounded-md px-8 py-3.5 mt-7 hover:brightness-95 transition-all">
          <MessageCircle size={18} /> Adquiere tu plan ahora
        </a>
      </motion.div>
    </Section>
  );
}

function Footer() {
  const brandName = useBranding();
  return (
    <footer className="border-t border-white/10 mt-10">
      <Section className="py-10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png?v=20260724-groups" alt={brandName} className="w-10 h-10 rounded object-contain" />
          <span className="font-heading font-bold" data-testid="footer-brand-name">{brandName}</span>
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Target size={14} className="text-gold" /> Datos oficiales de Supercell · Clash of Clans
        </div>
        <div className="flex items-center gap-4 text-sm">
          <a href="/admin" className="text-muted-foreground hover:text-white transition-colors">Panel</a>
          <a href={getWaLink(brandName)} target="_blank" rel="noreferrer" className="text-gold hover:brightness-110 flex items-center gap-1.5"><MessageCircle size={15} /> +1 786 874 0733</a>
        </div>
      </Section>
      <div className="text-center text-xs text-muted-foreground pb-6">
        © {new Date().getFullYear()} {brandName}. No afiliado oficialmente con Supercell.
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      <Nav />
      <Hero />
      <TrustBar />
      <Features />
      <AutomatedAlerts />
      <Security />
      <HowItWorks />
      <CommandCatalog />
      <Pricing />
      <Faq />
      <FinalCta />
      <Footer />
    </div>
  );
}
